import {
  Directive,
  Input,
  ElementRef,
  forwardRef,
  HostListener,
  OnInit,
  Renderer2,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * CurrencyInputDirective
 *
 * Use on any <input> to get live BRL formatting (R$ 1.234,56).
 * The ngModel / FormControl value is always a plain JS number.
 *
 * Usage:
 *   <input currencyInput [(ngModel)]="form.price" />
 *   <input currencyInput formControlName="price" />
 */
@Directive({
  selector: 'input[currencyInput]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputDirective),
      multi: true,
    },
  ],
})
export class CurrencyInputDirective implements ControlValueAccessor, OnInit {
  @Input() preserveEmpty = false;
  private onChange: (value: number | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef<HTMLInputElement>, private renderer: Renderer2) {}

  ngOnInit(): void {
    this.renderer.setAttribute(this.el.nativeElement, 'type', 'text');
    this.renderer.setAttribute(this.el.nativeElement, 'inputmode', 'numeric');
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────

  writeValue(value: number | null | undefined): void {
    if (this.preserveEmpty && value == null) { this.el.nativeElement.value = ''; return; }
    const numeric = value == null || isNaN(value as number) ? 0 : Number(value);
    this.el.nativeElement.value = this._format(numeric);
  }

  registerOnChange(fn: (v: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  // ── Event handlers ────────────────────────────────────────────────────────

  @HostListener('input')
  onInput(): void {
    const raw = this.el.nativeElement.value;
    const digits = raw.replace(/\D/g, '');

    if (digits === '' && this.preserveEmpty) { this.el.nativeElement.value = ''; this.onChange(null); return; }
    if (digits === '' || digits === '0') {
      this.el.nativeElement.value = this._format(0);
      this.onChange(0);
      return;
    }

    // Treat digits as cents: last 2 are decimals
    const cents = parseInt(digits, 10);
    const numeric = cents / 100 * (this.preserveEmpty && raw.includes('-') ? -1 : 1);

    this.el.nativeElement.value = this._format(numeric);
    this.onChange(numeric);
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
    const raw = this.el.nativeElement.value;
    const digits = raw.replace(/\D/g, '');
    if (digits === '' || digits === '0') {
      this.el.nativeElement.value = '';
    }
  }

  @HostListener('focus')
  onFocus(): void {
    // Move cursor to end
    const el = this.el.nativeElement;
    setTimeout(() => {
      el.selectionStart = el.selectionEnd = el.value.length;
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private _format(value: number): string {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
